import { ScrollArea } from '@radix-ui/react-scroll-area'
import { getCompanyInitials, TSpace, SpaceSidebarColumn } from '@valuecase/common'
import { DateTime } from 'luxon'
import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { AvatarThumbnail } from '../../components/ui/avatar-thumbnail'
import { Button2 } from '../../components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs'
import { IonIcon } from '../../Icons/IonIcon'
import { useBreakpoint } from '../../utils/useBreakpoint'
import ViewSettingsPopover from './components/ViewSettingsPopover'
import { useSpaceSidebar } from './context/SpaceSidebarDataContext'
import ActivitiesTab from './tabs/ActivitiesTab'
import CommentsTab from './tabs/CommentsTab'
import FormsTab from './tabs/FormsTab'
import TasksTab from './tasks/TasksTab'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateHeader,
  EmptyStateTitle,
} from '../../components/ui'

type TabValue = SpaceSidebarColumn

export const SpaceSheetContent = ({
  space,
  onClose,
}: {
  space: (TSpace & { data: any }) | undefined
  onClose: () => void
}) => {
  const [activeTab, setActiveTab] = useState<TabValue | null>(null)
  const {
    unreadActivitiesCount,
    unreadCommentsCount,
    onMarkActivitiesAsRead,
    onMarkCommentsAsRead,
    onOpenSpace,
    onCopySpaceLink,
    contextMenu,
    viewSettings,
    onUpdateViewSettings,
  } = useSpaceSidebar()
  const containerRef = useRef<HTMLDivElement>(null)
  const isNarrow = useBreakpoint(280, containerRef)

  const handleTabChange = useCallback(
    async (value: string) => {
      setActiveTab(value as TabValue)
      if (value === 'activities' && unreadActivitiesCount > 0) {
        onMarkActivitiesAsRead?.()
      }
      if (value === 'comments' && unreadCommentsCount > 0) {
        await onMarkCommentsAsRead?.()
      }
    },
    [onMarkActivitiesAsRead, onMarkCommentsAsRead, unreadActivitiesCount, unreadCommentsCount],
  )

  const avatar = useMemo(() => {
    if (space?.companyLogo) {
      return <AvatarThumbnail type='image' imageUrl={space?.companyLogo} size={'LARGE'} />
    }
    const initials = getCompanyInitials(space?.companyName ?? '')?.toLowerCase()

    return <AvatarThumbnail type='gravatarInitials' initials={initials} size={'LARGE'} />
  }, [space?.companyLogo, space?.companyName])

  const orderedTabs = useMemo(() => {
    const tabs = [
      { id: 'tasks' as const, label: 'Tasks', component: <TasksTab /> },
      { id: 'activities' as const, label: 'Activities', component: <ActivitiesTab /> },
      { id: 'comments' as const, label: 'Comments', component: <CommentsTab /> },
      { id: 'forms' as const, label: 'Forms', component: <FormsTab /> },
    ]

    if (!viewSettings) {
      return tabs
    }

    // Filter out hidden tabs and sort by order
    return tabs
      .filter(
        (tab) => (viewSettings.settings.visible as Record<SpaceSidebarColumn, boolean>)[tab.id],
      )
      .sort((a, b) => {
        const orderA = (viewSettings.settings.order as SpaceSidebarColumn[]).indexOf(a.id)
        const orderB = (viewSettings.settings.order as SpaceSidebarColumn[]).indexOf(b.id)
        return orderA - orderB
      })
  }, [viewSettings])

  // Handle tab selection when orderedTabs changes or when current tab is not available
  useEffect(() => {
    if (orderedTabs.length === 0) return
    if (!orderedTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(orderedTabs[0].id)
    }
  }, [orderedTabs, activeTab])

  return (
    <div className='flex flex-col w-full h-full' ref={containerRef}>
      {isNarrow ? (
        <div className='flex flex-col p-6 gap-2 bg-white'>
          <div className='flex justify-end gap-2'>
            {contextMenu}
            <Button2
              variant='ghost'
              size={'smallIcon'}
              leadingIcon='c_open'
              onClick={onOpenSpace}
            />
            <Button2
              variant='ghost'
              size={'smallIcon'}
              leadingIcon='c_link'
              onClick={onCopySpaceLink}
            />
            <Button2 variant='ghost' size={'smallIcon'} leadingIcon='close' onClick={onClose} />
          </div>
          <div className='flex items-center gap-2'>
            {avatar}
            <span className='text-lg font-bold text-grey-s6 truncate'>{space?.companyName}</span>
          </div>
        </div>
      ) : (
        <div className='flex items-center justify-between p-6 bg-white'>
          <div className='flex items-center gap-2'>
            {avatar}
            <span className='text-lg font-bold text-grey-s6'>{space?.companyName}</span>
          </div>
          <div className='flex items-center gap-2'>
            {contextMenu}
            <Button2
              variant='ghost'
              size={'smallIcon'}
              leadingIcon='c_open'
              onClick={onOpenSpace}
            />
            <Button2
              variant='ghost'
              size={'smallIcon'}
              leadingIcon='c_link'
              onClick={onCopySpaceLink}
            />
            <Popover>
              <PopoverTrigger asChild>
                <Button2 variant='ghost' size={'smallIcon'} leadingIcon='c_view-settings-filled' />
              </PopoverTrigger>
              <PopoverContent hideClose className='p-3' align='end' alignOffset={-30}>
                {viewSettings && (
                  <ViewSettingsPopover
                    viewSettings={viewSettings}
                    onUpdateViewSettings={onUpdateViewSettings}
                  />
                )}
              </PopoverContent>
            </Popover>

            <Button2 variant='ghost' size={'smallIcon'} leadingIcon='close' onClick={onClose} />
          </div>
        </div>
      )}
      {!isNarrow && (
        <div className='flex flex-col px-6 gap-1 bg-white pb-6'>
          <div className='flex gap-1'>
            <span className='text-grey-s5 text-sm'>Owner:</span>
            <span className='text-grey-s6 text-sm font-semibold'>
              {space?.owner?.firstName + ' ' + space?.owner?.lastName}
            </span>
          </div>
          <div className='flex gap-1'>
            <span className='text-grey-s5 text-sm'>Visits:</span>
            <span className='text-grey-s6 text-sm font-semibold'>{space?.visits}</span>
            <span className='text-grey-s5 text-sm mx-2'>•</span>
            <span className='text-grey-s5 text-sm'>Last visited:</span>
            <span className='text-grey-s6 text-sm font-semibold'>
              {space?.lastVisit
                ? DateTime.fromJSDate(new Date(space?.lastVisit)).toFormat('dd MMM yyyy')
                : 'N/A'}
            </span>
          </div>
        </div>
      )}

      {orderedTabs.length === 0 ? (
        <div className='flex w-full justify-center align-middle p-5'>
          <EmptyState
            icon='c_view-settings-filled'
            iconClassName='text-blue-s3'
            className='p-4 gap-4'
          >
            <EmptyStateHeader className='max-w-[312px]'>
              <EmptyStateTitle>No tabs to display</EmptyStateTitle>
              <EmptyStateDescription>
                All tabs are currently hidden in view settings.
              </EmptyStateDescription>
            </EmptyStateHeader>
          </EmptyState>
        </div>
      ) : (
        <Tabs
          value={activeTab ?? undefined}
          onValueChange={handleTabChange}
          className='flex flex-col overflow-hidden'
        >
          <TabsList className='w-full flex justify-between px-0 bg-white rounded-none'>
            {orderedTabs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className='flex-1 flex gap-2 text-grey-s5 justify-center'
              >
                <IonIcon
                  name={
                    tab.id === 'activities'
                      ? 'stats-chart'
                      : tab.id === 'comments'
                        ? 'chatbubble'
                        : tab.id === 'forms'
                          ? 'document-text'
                          : 'clipboard'
                  }
                />
                {!isNarrow && tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <ScrollArea className='flex-1 border-t border-grey-s2 overflow-auto'>
            {orderedTabs.map((tab) => (
              <TabsContent key={tab.id} value={tab.id} className='m-0'>
                {tab.component}
              </TabsContent>
            ))}
          </ScrollArea>
        </Tabs>
      )}
    </div>
  )
}

export default SpaceSheetContent
